import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import { useApolloClient, gql } from '@apollo/client';

const IdleContext = createContext({
  countdown: 600,
});

export const useIdle = () => useContext(IdleContext);

const IDLE_TIMEOUT_SECONDS = 10 * 60; // 10 minutes
const HEARTBEAT_INTERVAL = 3 * 60 * 1000; // 3 minutes for reliable 10-min sync

// A simple minimal query to refresh the backend session
const HEARTBEAT_QUERY = gql`
  query Heartbeat {
    __typename
  }
`;

export const IdleProvider = ({ children }) => {
  const [countdown, setCountdown] = useState(IDLE_TIMEOUT_SECONDS);
  const countdownRef = useRef(IDLE_TIMEOUT_SECONDS);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const client = useApolloClient();
  const lastHeartbeatRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    const token = secureLocalStorage.getItem('token');
    if (!token) return;

    console.log('Idle timeout reached. Logging out from Provider...');
    localStorage.removeItem('admin-info');
    secureLocalStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  const sendHeartbeat = useCallback(async () => {
    try {
      const token = secureLocalStorage.getItem('token');
      if (token && pathname !== '/login') {
        console.log('Sending session heartbeat...');
        await client.query({ query: HEARTBEAT_QUERY, fetchPolicy: 'network-only' });
        lastHeartbeatRef.current = Date.now();
      }
    } catch (error) {
      console.warn('Heartbeat failed, session may have expired:', error);
    }
  }, [client, pathname]);

  useEffect(() => {
    const token = secureLocalStorage.getItem('token');
    const isLoginPage = pathname === '/login';

    if (!token || isLoginPage) {
      setCountdown(IDLE_TIMEOUT_SECONDS);
      countdownRef.current = IDLE_TIMEOUT_SECONDS;
      return;
    }

    // Interval to decrement the countdown every second
    const interval = setInterval(() => {
      countdownRef.current -= 1;
      const currentVal = countdownRef.current;

      if (currentVal <= 0) {
        clearInterval(interval);
        handleLogout();
        return;
      }
      
      // Update UI state
      setCountdown(currentVal);
      
      // Every few minutes, if active, send a heartbeat to keep the backend alive
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_INTERVAL) {
        sendHeartbeat();
      }
    }, 1000);

    // Reset the countdown on any user activity
    const activityHandler = () => {
      countdownRef.current = IDLE_TIMEOUT_SECONDS;
      setCountdown(IDLE_TIMEOUT_SECONDS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => window.addEventListener(event, activityHandler));

    return () => {
      clearInterval(interval);
      events.forEach((event) => window.removeEventListener(event, activityHandler));
    };
  }, [handleLogout, pathname]);

  return (
    <IdleContext.Provider value={{ countdown }}>
      {children}
    </IdleContext.Provider>
  );
};
